import { useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import {
  fetchCampaignAchievementRecipients,
  type CampaignAchievementRecipients as Recipients,
  type ListableAchievementId,
} from '@/lib/campaign-gamification'
import styles from './CampaignAchievementRecipients.module.css'

type Props = {
  campaignId: number
  badge: ListableAchievementId
  badgeName: string
  token?: string
}

export function CampaignAchievementRecipients({
  campaignId,
  badge,
  badgeName,
  token,
}: Props) {
  const [expanded, setExpanded] = useState(false)
  const [data, setData] = useState<Recipients | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const request = useRef<AbortController | null>(null)
  const pendingPage = useRef(1)
  const panelId = `badge-${campaignId}-${badge}-recipients`

  useEffect(() => () => request.current?.abort(), [])

  const load = async (page: number) => {
    request.current?.abort()
    const controller = new AbortController()
    request.current = controller
    pendingPage.current = page
    setLoading(true)
    setError(false)
    try {
      const result = await fetchCampaignAchievementRecipients(
        campaignId,
        badge,
        page,
        token,
        controller.signal
      )
      if (!controller.signal.aborted) setData(result)
    } catch {
      if (!controller.signal.aborted) setError(true)
    } finally {
      if (!controller.signal.aborted) setLoading(false)
    }
  }

  const toggle = () => {
    if (expanded) {
      request.current?.abort()
      setExpanded(false)
      setLoading(false)
    } else {
      setExpanded(true)
      setData(null)
      void load(1)
    }
  }

  return (
    <div className={styles.container}>
      <button
        type="button"
        className={styles.toggle}
        onClick={toggle}
        aria-expanded={expanded}
        aria-controls={panelId}
        aria-label={`${expanded ? 'Recolher' : 'Exibir'} lista de clientes: ${badgeName}`}
      >
        <Icon icon="mdi:account-group-outline" aria-hidden />
        <span>{expanded ? 'Recolher lista' : 'Exibir lista'}</span>
        <Icon icon={expanded ? 'mdi:chevron-up' : 'mdi:chevron-down'} aria-hidden />
      </button>
      {expanded && (
        <section
          id={panelId}
          className={styles.panel}
          aria-label={`Clientes com o selo ${badgeName}`}
          aria-busy={loading}
        >
          <div className={styles.heading}>
            <h3>Clientes que conquistaram este selo</h3>
            {data && (
              <span className={styles.count}>
                {data.pagination.total}{' '}
                {data.pagination.total === 1 ? 'cliente' : 'clientes'}
              </span>
            )}
          </div>
          {badge === 'profile_complete' && (
            <p className={styles.hint}>
              O perfil completo pertence à conta Sortee e vale para todas as campanhas.
            </p>
          )}
          {loading ? (
            <p className={styles.feedback} role="status">
              Carregando clientes…
            </p>
          ) : error ? (
            <div className={styles.feedback}>
              <p role="alert">Não foi possível carregar a lista de clientes.</p>
              <button type="button" onClick={() => void load(pendingPage.current)}>
                Tentar novamente
              </button>
            </div>
          ) : data && data.donors.length ? (
            <>
              <ul className={styles.list}>
                {data.donors.map((donor) => (
                  <li key={donor.id} className={styles.row}>
                    <div className={styles.identity}>
                      <strong>{donor.name}</strong>
                      <span>Cliente #{donor.id}</span>
                    </div>
                    <div className={styles.contact}>
                      <span>{donor.phone ?? 'WhatsApp não informado'}</span>
                      {donor.email && <span>{donor.email}</span>}
                    </div>
                    <Link
                      to={`/clientes/${donor.id}/editar`}
                      aria-label={`Ver ficha de ${donor.name}, cliente ${donor.id}`}
                    >
                      Ver ficha <Icon icon="mdi:arrow-top-right" aria-hidden />
                    </Link>
                  </li>
                ))}
              </ul>
              <div className={styles.pagination}>
                <span>
                  Página {data.pagination.current_page} de {data.pagination.last_page}
                </span>
                <div>
                  <button
                    type="button"
                    disabled={data.pagination.current_page <= 1}
                    onClick={() => void load(data.pagination.current_page - 1)}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    disabled={data.pagination.current_page >= data.pagination.last_page}
                    onClick={() => void load(data.pagination.current_page + 1)}
                  >
                    Próxima
                  </button>
                </div>
              </div>
            </>
          ) : (
            <p className={styles.feedback}>
              Nenhum cliente conquistou este selo com as regras atuais.
            </p>
          )}
        </section>
      )}
    </div>
  )
}
